import { Loc } from 'main.core';
import {
	SharedApi,
	LoginValidator,
} from 'b24network.vue.cross-scenario-shared-js';

export function mapRawAccountToRestoreAccount(rawAccount: ?Object): ?Object
{
	if (!rawAccount?.LOGIN || !rawAccount?.ID)
	{
		return null;
	}

	return {
		id: rawAccount.ID,
		email: rawAccount.EMAIL,
		login: rawAccount.LOGIN,
		avatarUrl: rawAccount.PERSONAL_PHOTO ?? '',
		fullName: rawAccount.NAME
			? `${rawAccount.NAME} ${rawAccount.LAST_NAME}`
			: '',
	};
}

export function pickRawAccountForLogin(rawAccounts: Array, login: string): ?Object
{
	if (rawAccounts.length === 1)
	{
		return rawAccounts[0];
	}

	return rawAccounts.find((account) => account.LOGIN === login) ?? null;
}

export function extractResendTimeout(restoreResult: ?Object): number
{
	return restoreResult?.TIMEOUT ?? restoreResult?.DATE_SEND ?? 0;
}

const PASSWORD_RESTORE_CAPTCHA_PREFETCH_ERROR = 'PASSWORD_RESTORE_CAPTCHA_PREFETCH_ERROR';

export function createPasswordRestoreCaptchaPrefetchError(
	{
		login,
		account,
		errorResponse,
	}: {
		login: string,
		account: Object,
		errorResponse: Object,
	},
): Error
{
	const error = new Error(PASSWORD_RESTORE_CAPTCHA_PREFETCH_ERROR);
	error.login = login;
	error.account = account;
	error.errorResponse = errorResponse;

	return error;
}

export function isPasswordRestoreCaptchaPrefetchError(error: mixed): boolean
{
	return error instanceof Error && error.message === PASSWORD_RESTORE_CAPTCHA_PREFETCH_ERROR;
}

export async function requestPasswordRestoreData(
	{
		login,
		defaultCountry,
		captchaResult = null,
	}: {
		login: string,
		defaultCountry: string,
		captchaResult?: ?Object,
	},
): Promise<Object>
{
	const accountResponse = await SharedApi.loadAccountsByLogin(login, defaultCountry);
	const rawAccounts = accountResponse.data ?? [];
	const rawAccount = pickRawAccountForLogin(rawAccounts, login);
	const account = mapRawAccountToRestoreAccount(rawAccount);

	if (!account)
	{
		throw new Error(Loc.getMessage('EXT_B24NET_LOGGING_IN_NOT_FOUND_EMAIL'));
	}

	let restoreResult = null;

	try
	{
		const response = await SharedApi.restorePassword(
			login,
			LoginValidator.isPhoneLogin(login),
			captchaResult,
		);
		restoreResult = response.data ?? null;
	}
	catch (errorResponse)
	{
		const hasCaptchaError = (errorResponse?.errors ?? [])
			.some((err) => err.code === 'ERROR_CAPTCHA')
		;

		if (hasCaptchaError)
		{
			throw createPasswordRestoreCaptchaPrefetchError({
				login: account.login ?? login,
				account,
				errorResponse,
			});
		}

		throw errorResponse;
	}

	return {
		login: account.login ?? login,
		account,
		restoreResult,
		resendTimeout: extractResendTimeout(restoreResult),
	};
}
